/**
 * Ticker — running text, right to left, in white. Presentational only: it is
 * handed its text, so the sidebar and the settings page's live preview both
 * render the one thing instead of two that could drift.
 *
 * Seamless loop: two copies in one track, and the track slides left by one
 * copy's width. The copy is at least as wide as the box (tickerTiming), so even
 * a four-word message crosses the whole sidebar instead of sitting in its
 * corner. The second copy is aria-hidden, or a screen reader says everything
 * twice.
 *
 * It stops for anyone who has asked their system for less motion — the text is
 * then shown still and cut with an ellipsis, and the full line is in the
 * tooltip. It also stops while hovered or focused, so a long message can be
 * read; the box is a tab stop for exactly that reason, since moving text that
 * cannot be paused is a problem for people who do not use a mouse.
 */

import { useLayoutEffect, useEffect, useRef, useState } from "react";
import { tickerTiming, TICKER_GAP_PX, type ITickerTiming } from "./tickerTiming";

// useLayoutEffect measures before the first paint, so the text never flashes at
// the default speed; it warns when rendered on a server, where there is no
// layout to measure and a plain effect is the same thing.
const useIsoLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

export function Ticker({ text, speed, title }: { text: string; speed: number; title?: string }) {
  const boxRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const [timing, setTiming] = useState<ITickerTiming | null>(null);

  useIsoLayoutEffect(() => {
    const box = boxRef.current;
    const span = textRef.current;
    if (!box || !span) return;
    const measure = () => {
      const next = tickerTiming(span.getBoundingClientRect().width, box.clientWidth, speed);
      // Same answer = same state, so a ResizeObserver tick cannot start a loop.
      setTiming((prev) =>
        prev && next && prev.copyW === next.copyW && prev.durationS === next.durationS ? prev : next);
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(box);
    ro.observe(span);
    return () => ro.disconnect();
  }, [text, speed]);

  const vars = {
    "--ticker-gap": `${TICKER_GAP_PX}px`,
    ...(timing ? { "--ticker-copy-w": `${timing.copyW}px`, "--ticker-dur": `${timing.durationS}s` } : {}),
  } as React.CSSProperties;

  return (
    <div
      ref={boxRef}
      className="sidebar-ticker"
      role="marquee"
      aria-label={text}
      tabIndex={0}
      title={title ?? text}
      style={vars}
    >
      <div className="sidebar-ticker-track">
        <span className="sidebar-ticker-copy"><span ref={textRef}>{text}</span></span>
        <span className="sidebar-ticker-copy" aria-hidden="true"><span>{text}</span></span>
      </div>
    </div>
  );
}
